import {PageHeader} from "../menu/PageHeader";
import {Button, Card, Col, Container, Form, Row} from "react-bootstrap";
import React, {useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {UsacmContext} from "../App";
import {
  applyCourseCartCoupon,
  getCourseCart,
  removeCourseCartCoupon,
  removeFromCourseCart,
  saveCourseCartReview
} from "../api/CourseApi";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {CONF_LABEL_KEY_COURSE, COURSE_REVIEW_FILE_MAX_BYTES} from "../constants";
import {centsToDollarStrWSign, getConfLabel, makePlural, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {getCoursePriceAccessStr, getCoursePriceAmountStr} from "./courseUtils";
import {CourseCheckout} from "./CourseCheckout";
import "./CourseList.scss";

/**
 * The user's course cart - their in progress CourseOrder.  Courses are added to it from the catalog (CourseList).
 * A line whose price needs review takes a note (and the proof file when the price requires one) before checkout.
 * One coupon code can be applied - its discount is a line of its own above the total.
 */
export function CourseCart() {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [navChanged,] = context.navigationChanged;
  const [, setCourseCartCount] = context.courseCartCount;
  const navigate = useNavigate();
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const [cart, setCart] = useState(null);
  const [errors, setErrors] = useState([]);
  const [checkingOut, setCheckingOut] = useState(false);

  // Keeps the menu's cart count in step - the menu hides the cart when it's empty
  function updateCart(data) {
    setCart(data);
    setCourseCartCount(data.items.length);
  }

  function loadCart() {
    getCourseCart((status, data, newErrors) => {
      if (status === 200) {
        updateCart(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  useEffect(() => {
    setCheckingOut(false);
    loadCart();
  }, [navChanged]);

  function callRemove(item) {
    removeFromCourseCart(item.id, (status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast(`Removed "${item.course_title}" from your cart.`);
        updateCart(data);
        setErrors([]);
      } else {
        setErrors(newErrors);
      }
    });
  }

  function cancelCheckout() {
    setCheckingOut(false);
    // The total may have changed while they were on the payment step
    loadCart();
  }

  const items = cart?.items || [];
  const hasProblems = items.some(item => !!item.problem) || !!cart?.coupon?.problem;
  const needsFiles = items.some(item => item.needs_file);
  const canCheckout = items.length > 0 && !hasProblems && !needsFiles && cart.amount >= 0;

  return (
    <div>
      <PageHeader pageTitle="Cart"/>
      <Container fluid className="usacm-container-medium">

        <div className="mb-3">
          <Button type="button" variant="link" className="ps-0" onClick={() => navigate('/course-list')}>
            <FontAwesomeIcon icon="fa-arrow-left" className="me-2"/>{makePlural(courseLabel)}
          </Button>
        </div>

        {getErrorMessageForField(errors, '') &&
          <Row>
            <Col className="text-center mb-3 usacm-error-message">
              {getErrorMessageForField(errors, '')}
            </Col>
          </Row>
        }

        {cart && items.length === 0 &&
          <Row>
            <Col className="text-center">
              Your cart is empty.
            </Col>
          </Row>
        }

        {items.map(item =>
          <Card key={item.id} className="mb-3">
            <Card.Body className="d-flex flex-wrap align-items-center gap-3">
              {item.image_url && <img src={item.image_url} alt="" className="course-cart-image"/>}
              <div className="flex-grow-1">
                <div className="fw-semibold">{item.course_title}</div>
                {item.course_subtitle && <div className="small text-muted">{item.course_subtitle}</div>}
                {item.course_price &&
                  <div className="small">
                    {item.course_price.name} &middot; {getCoursePriceAccessStr(item.course_price)}
                    {item.course_price.requires_review && <span> &middot; Requires approval</span>}
                  </div>
                }
                {item.problem && <div className="small usacm-error-message">{item.problem}</div>}
              </div>
              <div className="fw-semibold course-catalog-amount">{getCoursePriceAmountStr(item.item_amount)}</div>
              {!checkingOut &&
                <Button type="button" variant="outline-danger" size="sm" onClick={() => callRemove(item)}>
                  <FontAwesomeIcon icon="fa-trash" className="me-1"/>Remove
                </Button>
              }
            </Card.Body>
            {item.course_price?.requires_review &&
              <Card.Footer>
                <CourseCartReview item={item}
                                  readOnly={checkingOut}
                                  onSaved={(data) => updateCart(data)}/>
              </Card.Footer>
            }
          </Card>
        )}

        {items.length > 0 &&
          <>
            <CourseCartCoupon cart={cart} readOnly={checkingOut} onChanged={(data) => updateCart(data)}/>
            <div className="d-flex justify-content-end align-items-center gap-3 mt-4">
              <span className="fs-5">Total</span>
              <span className="fs-5 fw-semibold course-catalog-amount">{getCoursePriceAmountStr(cart.amount)}</span>
            </div>
            {!checkingOut &&
              <>
                <div className="d-flex justify-content-end mt-3">
                  <Button type="button" disabled={!canCheckout} onClick={() => setCheckingOut(true)}>Checkout</Button>
                </div>
                {hasProblems &&
                  <div className="text-end small text-muted mt-2">Remove the items with problems before checking out.</div>
                }
                {!hasProblems && needsFiles &&
                  <div className="text-end small text-muted mt-2">Upload the files needed for review before checking out.</div>
                }
              </>
            }
            {checkingOut && <CourseCheckout cart={cart} onCancel={() => cancelCheckout()}/>}
          </>
        }

      </Container>
    </div>
  );
}

/**
 * The coupon code line of the cart - the box to type a code in, and the discount once one is applied.
 * Applying another code replaces the coupon already on the cart.
 */
function CourseCartCoupon({cart, readOnly, onChanged}) {
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState([]);
  const coupon = cart.coupon;

  function callApply(e) {
    e.preventDefault();
    applyCourseCartCoupon(code, (status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast('Applied the coupon.');
        setCode('');
        setErrors([]);
        onChanged(data);
      } else {
        setErrors(newErrors);
      }
    });
  }

  function callRemove() {
    removeCourseCartCoupon((status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast('Removed the coupon.');
        setErrors([]);
        onChanged(data);
      } else {
        setErrors(newErrors);
      }
    });
  }

  return (
    <div className="mt-3">
      {coupon &&
        <Card className="mb-3">
          <Card.Body className="d-flex flex-wrap align-items-center gap-3">
            <FontAwesomeIcon icon="fa-tag" className="text-success"/>
            <div className="flex-grow-1">
              <div className="fw-semibold">{coupon.item_text}</div>
              {coupon.problem && <div className="small usacm-error-message">{coupon.problem}</div>}
            </div>
            <div className="fw-semibold course-catalog-amount">{centsToDollarStrWSign(coupon.item_amount)}</div>
            {!readOnly &&
              <Button type="button" variant="outline-danger" size="sm" onClick={() => callRemove()}>
                <FontAwesomeIcon icon="fa-trash" className="me-1"/>Remove
              </Button>
            }
          </Card.Body>
        </Card>
      }
      {!readOnly &&
        <Form onSubmit={callApply} className="d-flex justify-content-end align-items-start gap-2">
          <div>
            <Form.Control type="text"
                          size="sm"
                          placeholder="Coupon code"
                          aria-label="Coupon code"
                          value={code}
                          onChange={e => setCode(e.target.value)}
                          isInvalid={fieldHasErrors(errors, 'code')}/>
            <Form.Control.Feedback type="invalid">
              {getErrorMessageForField(errors, 'code')}
            </Form.Control.Feedback>
          </div>
          <Button type="submit" size="sm" variant="outline-primary" disabled={!code.trim()}>
            {coupon ? 'Change Coupon' : 'Apply Coupon'}
          </Button>
        </Form>
      }
      {getErrorMessageForField(errors, '') &&
        <div className="text-end small usacm-error-message mt-1">{getErrorMessageForField(errors, '')}</div>
      }
    </div>
  );
}

/**
 * The review part of a cart line - the price's instructions, the buyer's note, and the proof file when the price
 * requires one.  The note and file are saved together.
 */
function CourseCartReview({item, readOnly, onSaved}) {
  const coursePrice = item.course_price;
  const [reviewNote, setReviewNote] = useState(item.review_note || '');
  const [file, setFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0); // changing this clears the file input
  const [errors, setErrors] = useState([]);
  const changed = !!file || (reviewNote || '') !== (item.review_note || '');

  function chooseFile(newFile) {
    if (newFile && newFile.size > COURSE_REVIEW_FILE_MAX_BYTES) {
      setErrors([{message: `The file must be less than ${COURSE_REVIEW_FILE_MAX_BYTES / (1024 * 1024)}MB.`, fields: ['']}]);
      setFile(null);
      setFileInputKey(fileInputKey + 1);
      return;
    }
    setErrors([]);
    setFile(newFile);
  }

  function callSave() {
    saveCourseCartReview(item.id, reviewNote, file, (status, data, newErrors) => {
      if (status === 200) {
        showSuccessToast(file ? 'Uploaded your file.' : 'Saved your note.');
        setFile(null);
        setFileInputKey(fileInputKey + 1);
        setErrors([]);
        onSaved(data);
      } else {
        setErrors(newErrors);
      }
    });
  }

  return (
    <div className="small">
      <div className="fw-semibold mb-1">This price must be approved before you get access.</div>
      {coursePrice.review_instructions && <div className="mb-2">{coursePrice.review_instructions}</div>}

      {coursePrice.requires_file &&
        <div className="mb-2">
          {item.review_file_name ?
            <div><FontAwesomeIcon icon="fa-circle-check" className="me-1 text-success"/>Uploaded: {item.review_file_name}</div>
            :
            <div className="usacm-error-message">A file is required.</div>
          }
          {!readOnly &&
            <Form.Control key={fileInputKey}
                          type="file"
                          size="sm"
                          className="mt-1"
                          onChange={e => chooseFile(e.target.files[0] || null)}/>
          }
        </div>
      }

      {readOnly ?
        (item.review_note && <div>Note: {item.review_note}</div>)
        :
        <>
          <Form.Control as="textarea"
                        rows={2}
                        size="sm"
                        placeholder="A note for the reviewer (optional)"
                        value={reviewNote}
                        onChange={e => setReviewNote(e.target.value)}/>
          <div className="d-flex justify-content-end mt-2">
            <Button type="button" size="sm" disabled={!changed} onClick={() => callSave()}>
              {file ? 'Upload' : 'Save Note'}
            </Button>
          </div>
        </>
      }

      {getErrorMessageForField(errors, '') &&
        <div className="usacm-error-message mt-1">{getErrorMessageForField(errors, '')}</div>
      }
    </div>
  );
}
